import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';

/** Router-state key used to hand the block editor a one-off notice (e.g. "created as a draft"). */
export const BLOCK_EDITOR_NOTICE_STATE_KEY = 'blockEditorNotice';

/** A calm, non-error notice: the action that led here succeeded in part and the author can carry on. */
export interface BlockEditorNotice {
  message: string;
  tone: 'info' | 'warning';
}

/** Builds the `navigate(..., { state })` payload carrying a one-off editor notice. */
export function blockEditorNoticeState(notice: BlockEditorNotice): Record<string, BlockEditorNotice> {
  return { [BLOCK_EDITOR_NOTICE_STATE_KEY]: notice };
}

export function readBlockEditorNotice(state: unknown): BlockEditorNotice | null {
  if (typeof state !== 'object' || state === null) return null;
  const notice = (state as Record<string, unknown>)[BLOCK_EDITOR_NOTICE_STATE_KEY];
  if (typeof notice !== 'object' || notice === null) return null;
  const { message, tone } = notice as Record<string, unknown>;
  if (typeof message !== 'string' || !message) return null;
  return { message, tone: tone === 'warning' ? 'warning' : 'info' };
}

/**
 * Consumes the one-off notice handed over through router location state: reports it once via
 * `onNotice`, then clears the state with a `replace` navigation so a reload or back/forward to this
 * history entry never shows it again.
 */
export function useBlockEditorNoticeHandoff(onNotice: (notice: BlockEditorNotice) => void): void {
  const location = useLocation();
  const navigate = useNavigate();
  const onNoticeRef = useRef(onNotice);
  // Layout effects run before the passive effect below, so it always reads the latest callback.
  useLayoutEffect(() => {
    onNoticeRef.current = onNotice;
  }, [onNotice]);
  const notice = readBlockEditorNotice(location.state);
  const message = notice?.message;
  const tone = notice?.tone;
  const { pathname, search, hash, key } = location;

  useEffect(() => {
    if (!message || !tone) return;
    onNoticeRef.current({ message, tone });
    navigate({ pathname, search, hash }, { replace: true, state: null });
  }, [message, tone, key, pathname, search, hash, navigate]);
}
